import { lazy, Suspense } from "react";
import LazySection from "@/components/LazySection";
import { Link, useSearchParams } from "react-router-dom";
import ProductCard from "@/components/ProductCard";
import { useSiteTheme } from "@/hooks/useSiteTheme";
import DynamicBanner from "@/components/DynamicBanner";
import { useDesignProducts } from "@/hooks/useDesignProducts";

const FeaturesBanner = lazy(() => import("@/components/FeaturesBanner"));
const Categories = lazy(() => import("@/components/Categories"));

import HeroBanner from "@/components/HeroBanner";

const Design1 = () => {
  const { theme } = useSiteTheme();
  const [searchParams] = useSearchParams();
  const previewShowHero = searchParams.get("preview_show_hero");
  const shouldShowHero = previewShowHero === null ? true : previewShowHero === "1";
  // ★ Removed `categories: true` — categories are rendered by <Categories> below from shared cache (useShopCategories), avoiding a duplicate fetch.
  const { newProducts, bestProducts, allProducts } = useDesignProducts({ designId: "1", newLimit: 6, bestLimit: 6, allLimit: 8 });

  return (<>
    {shouldShowHero && <HeroBanner />}
    
    <DynamicBanner placement="top" />
    <DynamicBanner placement="middle" />
    <DynamicBanner placement="bottom" />
    
    {/* ★ Defer Categories render until visible to reduce initial JS work and request fan-out */}
    <LazySection height="200px" rootMargin="200px">
      <Suspense fallback={null}>
        <Categories enabled />
      </Suspense>
    </LazySection>

    {newProducts.length > 0 && (
      <section data-section-id="new_arrivals" className="py-10 md:py-20" style={{ backgroundColor: `hsl(${theme.sectionBg})` }}>
        <div className="container mx-auto px-4" style={{ maxWidth: "1440px" }}>
          <header className="text-center mb-8 md:mb-12">
            <p className="text-[10px] tracking-[0.4em] uppercase mb-2" style={{ color: `hsl(${theme.cardAccent})`, fontFamily: "var(--design-font-ui)", fontWeight: 600 }}>Fresh Drops</p>
            <h2 className="text-2xl md:text-4xl" style={{ color: `hsl(${theme.cardText})`, fontFamily: "var(--design-font-heading)", fontWeight: 700 }}>New Arrivals</h2>
          </header>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6">{newProducts.map((p) => <ProductCard key={p.id} product={p} />)}</div>
          <div className="text-center mt-8 md:mt-12"><Link to="/new-arrivals" className="inline-flex items-center gap-2 px-8 py-3 text-xs tracking-[0.3em] uppercase transition-all hover:scale-105" style={{ backgroundColor: `hsl(${theme.btnBg})`, color: `hsl(${theme.btnText})`, borderRadius: theme.btnRadius, fontFamily: "var(--design-font-ui)", fontWeight: 700 }}>View All →</Link></div>
        </div>
      </section>
    )}

    {bestProducts.length > 0 && (
      <section data-section-id="best_sellers" className="py-10 md:py-20" style={{ backgroundColor: `hsl(${theme.sectionAltBg})` }}>
        <div className="container mx-auto px-4" style={{ maxWidth: "1440px" }}>
          <header className="text-center mb-8 md:mb-12">
            <p className="text-[10px] tracking-[0.4em] uppercase mb-2" style={{ color: `hsl(${theme.cardAccent})`, fontFamily: "var(--design-font-ui)", fontWeight: 600 }}>Top Rated</p>
            <h2 className="text-2xl md:text-4xl" style={{ color: `hsl(${theme.cardText})`, fontFamily: "var(--design-font-heading)", fontWeight: 700 }}>Best Sellers</h2>
          </header>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">{bestProducts.map((p) => <ProductCard key={p.id} product={p} />)}</div>
          <div className="text-center mt-8 md:mt-12"><Link to="/best-selling" className="inline-flex items-center gap-2 px-8 py-3 text-xs tracking-[0.3em] uppercase transition-all hover:scale-105" style={{ backgroundColor: `hsl(${theme.btnBg})`, color: `hsl(${theme.btnText})`, borderRadius: theme.btnRadius, fontFamily: "var(--design-font-ui)", fontWeight: 700 }}>See All →</Link></div>
        </div>
      </section>
    )}

    {allProducts.length > 0 && (
      <section data-section-id="all_collections" className="py-10 md:py-20" style={{ backgroundColor: `hsl(${theme.sectionBg})` }}>
        <div className="container mx-auto px-4" style={{ maxWidth: "1440px" }}>
          <header className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl md:text-4xl" style={{ color: `hsl(${theme.cardText})`, fontFamily: "var(--design-font-heading)", fontWeight: 700 }}>Full Range</h2>
          </header>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-6">{allProducts.map((p) => <ProductCard key={p.id} product={p} />)}</div>
          <div className="text-center mt-8 md:mt-12"><Link to="/products" className="inline-flex items-center gap-2 px-8 py-3 text-xs tracking-[0.3em] uppercase border transition-all hover:scale-105" style={{ borderColor: `hsl(${theme.cardAccent})`, color: `hsl(${theme.cardText})`, borderRadius: theme.btnRadius, fontFamily: "var(--design-font-ui)", fontWeight: 700 }}>Browse All →</Link></div>
        </div>
      </section>
    )}
    <Suspense fallback={null}><FeaturesBanner /></Suspense>
  </>);
};
export default Design1;
